import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { Star, BadgeCheck } from "lucide-react";
import { SectionHeader } from "./SectionHeader";

const testimonials = [
  { name: "Rizky Pratama", role: "Programmer", img: 12, text: "Awalnya ragu karena harganya murah banget. Ternyata akun langsung aktif 10 menit setelah bayar. Coding assistant-nya kepake banget buat kerjaan harian." },
  { name: "Siti Nurhaliza", role: "Mahasiswa", img: 47, text: "NotebookLM-nya nyelamatin skripsiku. Upload 20 jurnal langsung dirangkum otomatis. Worth it banget buat mahasiswa tingkat akhir." },
  { name: "Andi Wijaya", role: "Pebisnis UMKM", img: 33, text: "Storage 5TB langsung masuk ke Drive. Sekarang semua data toko aman di cloud. Prosesnya cepat, dikirim via email sesuai janji." },
  { name: "Dewi Lestari", role: "Content Creator", img: 5, text: "Flow AI Video-nya keren, bisa bikin video promosi tanpa perlu editor. Gemini juga bantu bikin script konten tiap hari. Produktivitas naik drastis." },
  { name: "Budi Santoso", role: "Karyawan", img: 68, text: "Gmail sama Docs di kantor sekarang ada AI-nya. Bikin laporan kerja jadi 2x lebih cepat. Aktivasinya bener-bener otomatis, ga pake ribet." },
  { name: "Maya Anggraini", role: "Freelancer", img: 26, text: "Sebagai freelance writer, Deep Research-nya juara. Riset yang biasanya 3 jam sekarang cuma 15 menit. Sangat recommended!" },
  { name: "Fajar Ramadhan", role: "Digital Marketer", img: 15, text: "Kepake banget buat riset kompetitor dan bikin copy iklan. 18 bulan cuma 79rb itu gila sih, langganan bulanan tools lain aja lebih mahal." },
  { name: "Intan Permatasari", role: "Mahasiswa", img: 44, text: "Awalnya takut penipuan, ternyata beneran masuk email dalam 5 menit. Udah 2 bulan dipakai buat tugas kuliah, lancar terus." },
  { name: "Hendra Gunawan", role: "Programmer", img: 58, text: "Debugging jadi cepet banget. Gemini 3.1 Pro reasoning-nya udah kayak punya senior dev pribadi yang standby 24 jam." },
  { name: "Ratna Sari", role: "Pemilik UMKM", img: 31, text: "Dipakai buat balas email pelanggan dan bikin katalog produk pakai Docs AI. Suami saya sampai ikutan order satu lagi." },
  { name: "Yoga Prasetyo", role: "Content Creator", img: 60, text: "Bandingin sama langganan bulanan, ini jauh lebih hemat. Bisa login di HP dan laptop sekaligus tanpa masalah." },
  { name: "Nadia Rahma", role: "Freelancer", img: 9, text: "Sempat bingung waktu aktivasi, CS-nya responsif banget. Garansi bener-bener dijalankan. Puas dengan pelayanannya!" },
];

const Counter = ({ to, suffix = "", decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(mv, to, { duration: 1.6, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setVal(v) });
    return () => c.stop();
  }, [inView, to, mv]);
  return (
    <span ref={ref} className="tabular-nums">
      {decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("id-ID")}
      {suffix}
    </span>
  );
};

export const Testimonials = () => (
  <section className="py-20 md:py-28 bg-white" id="testimoni">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <SectionHeader
        title="Mereka sudah order. Ini kata mereka."
        sub="Testimoni asli dari pengguna di seluruh Indonesia — mahasiswa, freelancer, hingga pemilik bisnis."
      />

      <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-2xl mb-14">
        <div>
          <p className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">
            <Counter to={2500} suffix="+" />
          </p>
          <p className="text-xs md:text-sm text-slate-500 font-medium mt-1">Pengguna Aktif</p>
        </div>
        <div>
          <p className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">
            <Counter to={4.9} decimals={1} />
          </p>
          <p className="text-xs md:text-sm text-slate-500 font-medium mt-1">Rating Rata-rata</p>
        </div>
        <div>
          <p className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">
            <Counter to={98} suffix="%" />
          </p>
          <p className="text-xs md:text-sm text-slate-500 font-medium mt-1">Lebih Hemat</p>
        </div>
      </div>

      <div className="columns-1 md:columns-2 lg:columns-3 gap-5 space-y-5">
        {testimonials.map((t, i) => (
          <motion.div
            key={t.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="break-inside-avoid rounded-3xl bg-white border border-slate-200 p-6 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_14px_36px_rgba(37,99,235,0.1)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
            data-testid={`testimonial-card-${i}`}
          >
            <div className="flex gap-0.5 mb-3">
              {[...Array(5)].map((_, s) => (
                <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <p className="text-slate-700 text-sm leading-relaxed">"{t.text}"</p>
            <div className="flex items-center gap-3 mt-5">
              <img src={`https://i.pravatar.cc/80?img=${t.img}`} alt={t.name} width="40" height="40" className="w-10 h-10 rounded-full object-cover" loading="lazy" decoding="async" />
              <div>
                <p className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                  {t.name}
                  <BadgeCheck className="w-4 h-4 text-blue-500" />
                </p>
                <p className="text-xs text-slate-500">{t.role}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
